import { Fragment } from 'react'
import type { ActivityEvent } from '@workflow/contracts'
import { EmptyNote, Loading, StateNote } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { useActivity, useSession } from '@/lib/api/hooks'
import { formatRelative } from '@/lib/format'
import { PageFrame, Section } from '@/shell/PageFrame'
import { describeActivity } from './phrases'

const DAY_HEADER = new Intl.DateTimeFormat('en', { weekday: 'long', day: 'numeric', month: 'long' })

/**
 * Activity — the whole trail, on its own page. Not the audit's identifiers:
 * every row names the thing it happened to. Reading it is an administrator's ability on
 * the API, so for staff the page is the sentence that says who holds it.
 */
export function ActivityPage() {
  const session = useSession()
  const isAdmin = session.data?.account?.role === 'admin'
  const activity = useActivity()

  return (
    <PageFrame title="Activity" description="Everything the platform did, named.">
      {!isAdmin ? (
        <p className="max-w-[60ch] text-ui text-muted-foreground">
          The platform's activity — every sign-in, decision, and change — is kept as the
          administrators' record.
        </p>
      ) : (
        <>
          <Section title="The feed">
            {activity.isLoading ? (
              <Loading label="Loading the feed" />
            ) : activity.error || !activity.data ? (
              <StateNote title="The feed could not be loaded." detail={describeError(activity.error)} />
            ) : activity.data.events.length === 0 ? (
              <EmptyNote title="Nothing yet." detail="The first sign-in will appear here." />
            ) : (
              <DayGroups events={activity.data.events} />
            )}
          </Section>
        </>
      )}
    </PageFrame>
  )
}

/** The feed, cut into days: Today, yesterday, then the date itself. */
function DayGroups({ events }: { events: ActivityEvent[] }) {
  const today = new Date().toISOString().slice(0, 10)
  const yesterday = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10)

  const groups: { day: string; events: ActivityEvent[] }[] = []
  for (const event of events) {
    const day = event.createdAt.slice(0, 10)
    const last = groups[groups.length - 1]
    if (last && last.day === day) last.events.push(event)
    else groups.push({ day, events: [event] })
  }

  return (
    <div className="flex flex-col">
      {groups.map((group) => (
        <Fragment key={group.day}>
          <h3 className="text-micro mt-8 mb-1 text-muted-foreground uppercase first:mt-0">
            {group.day === today ? 'Today' : group.day === yesterday ? 'Yesterday' : DAY_HEADER.format(new Date(`${group.day}T00:00:00Z`))}
          </h3>
          <ul className="flex flex-col divide-y divide-hairline">
            {group.events.map((event) => (
              <ActivityRow key={event.id} event={event} />
            ))}
          </ul>
        </Fragment>
      ))}
    </div>
  )
}

/**
 * One row of the studio's day: who, what, to what, when. The action's words
 * come from the phrase table; the subject is the name the API resolved — a
 * job's title, a client, a person. An action with no phrase yet shows as
 * itself, in mono: identifiers are what mono is for.
 */
function ActivityRow({ event }: { event: ActivityEvent }) {
  const who = event.actorName ?? event.actorEmail ?? 'The platform'
  const phrase = describeActivity(event)

  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-2.5">
      <span className="text-ui font-medium text-foreground">{who}</span>
      {phrase.raw ? (
        <span className="font-mono text-[13px] text-muted-foreground">{phrase.pre}</span>
      ) : (
        <>
          <span className="text-ui text-muted-foreground">{phrase.pre}</span>
          {phrase.subject ? <span className="text-ui font-medium text-foreground">{phrase.subject}</span> : null}
          {phrase.post ? <span className="text-ui text-muted-foreground">{phrase.post}</span> : null}
        </>
      )}
      <span className="ml-auto shrink-0 text-caption text-muted-foreground" title={event.createdAt}>
        {formatRelative(event.createdAt)}
      </span>
    </li>
  )
}
